import { useState } from 'react'
import { isRememberMe, setRememberMe } from '../lib/authStorage'
import { useAuth } from '../context/AuthContext'

export default function LoginPage() {
  const { signIn, signUp, resetPassword } = useAuth()
  const [mode, setMode]       = useState('signin') // 'signin' | 'signup' | 'reset'
  const [email, setEmail]     = useState('')
  const [password, setPassword] = useState('')
  const [name, setName]       = useState('')
  const [remember, setRemember] = useState(() => isRememberMe())
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState(null)
  const [resetSent, setResetSent] = useState(false)
  const [signupSent, setSignupSent] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setLoading(true); setError(null)
    if (mode === 'reset') {
      const err = await resetPassword(email)
      if (err) setError(err.message)
      else setResetSent(true)
    } else if (mode === 'signup') {
      const err = await signUp(email, password)
      if (err) setError(err.message)
      else setSignupSent(true)
    } else {
      setRememberMe(remember) // must be set BEFORE signIn so the new session is stored in the right place
      const err = await signIn(email, password)
      if (err) setError(err.message)
    }
    setLoading(false)
  }

  /* ── inline styles ── */
  const S = {
    shell: {
      display: 'grid',
      gridTemplateColumns: '1.05fr 1fr',
      minHeight: '100vh',
    },
    /* ── LEFT PANEL ── */
    brand: {
      position: 'relative',
      overflow: 'hidden',
      background: '#0B1F3A',
      padding: '56px 64px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    bpGrid: {
      position: 'absolute', inset: 0, opacity: .5,
      backgroundImage: 'linear-gradient(rgba(93,173,226,.06) 1px,transparent 1px),linear-gradient(90deg,rgba(93,173,226,.06) 1px,transparent 1px)',
      backgroundSize: '38px 38px',
    },
    bpMark: {
      position: 'absolute', right: -130, bottom: -120,
      opacity: .07, pointerEvents: 'none',
    },
    bpTop: { position: 'relative', display: 'flex', alignItems: 'center', gap: 14 },
    wm: { fontFamily: "'Space Grotesk',Inter,sans-serif", fontWeight: 600, fontSize: 24, color: '#fff', letterSpacing: '-.02em', lineHeight: 1 },
    bpMid: { position: 'relative', maxWidth: 440 },
    bpTag: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 12, letterSpacing: '.16em', textTransform: 'uppercase', color: '#5DADE2', fontWeight: 500, marginBottom: 20 },
    bpH1: { fontFamily: "'Space Grotesk',Inter,sans-serif", fontWeight: 600, fontSize: 42, lineHeight: 1.08, letterSpacing: '-.03em', color: '#fff' },
    bpP: { fontSize: 16, color: '#9DB4CE', marginTop: 18, maxWidth: 400 },
    feats: { position: 'relative', display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 420 },
    feat: { display: 'flex', alignItems: 'flex-start', gap: 14 },
    featIc: { width: 34, height: 34, borderRadius: 9, background: 'rgba(93,173,226,.12)', border: '1px solid #1A3A5C', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    featTt: { fontFamily: "'Space Grotesk',Inter,sans-serif", fontWeight: 600, fontSize: 14, color: '#fff' },
    featSub: { fontSize: 13, color: '#7E97B3', marginTop: 2 },
    bpFoot: { position: 'relative', display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, color: '#5A7595' },
    dot: { width: 5, height: 5, borderRadius: '50%', background: '#2BB673', flexShrink: 0 },
    /* ── RIGHT PANEL ── */
    formPanel: { background: '#F5F8FD', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 32px' },
    formCard: { width: '100%', maxWidth: 392 },
    fcHead: { marginBottom: 0 },
    fcH2: { fontFamily: "'Space Grotesk',Inter,sans-serif", fontWeight: 600, fontSize: 30, letterSpacing: '-.02em', color: '#0B1F3A' },
    fcP: { fontSize: 14.5, color: '#5A6478', marginTop: 8 },
    seg: { display: 'flex', gap: 4, margin: '26px 0 24px', background: '#E7EEF8', border: '1px solid #E1E9F4', borderRadius: 11, padding: 4 },
    segBtn: (on) => ({
      flex: 1, padding: 10, borderRadius: 8, border: 'none',
      fontFamily: "'IBM Plex Sans',sans-serif", fontSize: 13, fontWeight: 600, cursor: 'pointer',
      color: on ? '#0B3D91' : '#5A6478',
      background: on ? '#fff' : 'transparent',
      boxShadow: on ? '0 1px 3px rgba(11,31,58,.08)' : 'none',
      transition: 'all .15s',
    }),
    form: { display: 'flex', flexDirection: 'column', gap: 18 },
    label: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, fontWeight: 500, letterSpacing: '.1em', textTransform: 'uppercase', color: '#5A6478', display: 'block', marginBottom: 8 },
    input: { width: '100%', padding: '13px 15px', background: '#fff', border: '1.5px solid #E1E9F4', borderRadius: 10, fontFamily: "'IBM Plex Sans',sans-serif", fontSize: 14.5, color: '#0B1F3A', outline: 'none', transition: 'border-color .15s, box-shadow .15s', boxSizing: 'border-box' },
    rowBetween: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: -4 },
    rememberLabel: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#5A6478', cursor: 'pointer', userSelect: 'none' },
    link: { fontSize: 13, color: '#0B3D91', fontWeight: 500, textDecoration: 'none' },
    submitBtn: (loading) => ({
      marginTop: 6, padding: 14, background: loading ? '#0a2d6e' : '#0B3D91', color: '#fff', border: 'none', borderRadius: 10,
      fontFamily: "'IBM Plex Sans',sans-serif", fontSize: 14.5, fontWeight: 600, cursor: loading ? 'wait' : 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'background .15s',
      width: '100%',
    }),
    fcFoot: { marginTop: 24, textAlign: 'center', fontSize: 12.5, color: '#A9B6C9' },
    errBox: { background: '#fff2f2', border: '1.5px solid #fca5a5', borderRadius: 10, padding: '11px 14px', marginBottom: 4, fontSize: 13, color: '#b91c1c' },
  }

  const features = [
    {
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M4 19V5M4 19h16M8 16v-5M13 16V8M18 16v-7" stroke="#5DADE2" strokeWidth="2" strokeLinecap="round"/></svg>,
      title: 'Live cost control',
      sub: 'Commitments vs. budget, updated as POs are raised.',
    },
    {
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M7 8h10M7 12h10M7 16h6" stroke="#5DADE2" strokeWidth="2" strokeLinecap="round"/><rect x="3" y="3" width="18" height="18" rx="3" stroke="#5DADE2" strokeWidth="2"/></svg>,
      title: 'Contracts & retention',
      sub: 'Every certificate, variation and retention release tracked.',
    },
    {
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 2v20M5 6l7-4 7 4M5 18l7 4 7-4" stroke="#5DADE2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
      title: 'Cash in / out forecast',
      sub: 'Project liquidity you can plan around, not guess at.',
    },
  ]

  return (
    <div style={S.shell}>

      {/* ── LEFT BRAND PANEL ── */}
      <aside style={S.brand}>
        <div style={S.bpGrid} />
        {/* giant watermark */}
        <svg style={S.bpMark} width="460" height="460" viewBox="0 0 100 100" aria-hidden="true">
          <path d="M50 18 A32 32 0 1 0 82 50 L68 50 A18 18 0 1 1 50 32 Z" fill="#5DADE2"/>
          <circle cx="73" cy="50" r="7.5" fill="#5DADE2"/>
        </svg>

        {/* top: logo */}
        <div style={S.bpTop}>
          <svg width="38" height="38" viewBox="0 0 100 100" aria-label="CostSpace">
            <rect width="100" height="100" rx="24" fill="#0B3D91"/>
            <path d="M50 18 A32 32 0 1 0 82 50 L68 50 A18 18 0 1 1 50 32 Z" fill="#FFFFFF"/>
            <circle cx="73" cy="50" r="7.5" fill="#5DADE2"/>
          </svg>
          <span style={S.wm}>Cost<span style={{color:'#5DADE2'}}>Space</span></span>
        </div>

        {/* mid: headline */}
        <div style={S.bpMid}>
          <div style={S.bpTag}>Project Cost Intelligence</div>
          <h1 style={S.bpH1}>Control Cost.<br/>Drive Performance</h1>
          <p style={S.bpP}>Budgets, Forecast, procurement and cash flow — one source of truth for your company</p>
        </div>

        {/* features */}
        <div style={S.feats}>
          {features.map((f, i) => (
            <div key={i} style={S.feat}>
              <span style={S.featIc}>{f.icon}</span>
              <div>
                <div style={S.featTt}>{f.title}</div>
                <div style={S.featSub}>{f.sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* footer */}
        <div style={S.bpFoot}>
          <span style={S.dot}/>
          All systems operational · CostSpace v2.0
        </div>
      </aside>

      {/* ── RIGHT FORM PANEL ── */}
      <main style={S.formPanel}>
        <div style={S.formCard}>

          <div style={S.fcHead}>
            <h2 style={S.fcH2}>{mode === 'signin' ? 'Welcome back' : mode === 'reset' ? 'Reset your password' : 'Create your account'}</h2>
            <p style={S.fcP}>
              {mode === 'signin'
                ? 'Sign in to continue to your project workspace.'
                : mode === 'reset'
                ? 'Enter your email and we will send you a reset link.'
                : 'Get set up on your project workspace in a minute.'}
            </p>
          </div>

          {/* tab switcher */}
          {mode !== 'reset' && (
            <div style={S.seg}>
              {['signin','signup'].map(m => (
                <button key={m} type="button" style={S.segBtn(mode===m)} onClick={()=>{setMode(m);setError(null);setSignupSent(false)}}>
                  {m==='signin' ? 'Sign In' : 'Sign Up'}
                </button>
              ))}
            </div>
          )}

          {error && (
            <div style={S.errBox}>{error}</div>
          )}

          {mode === 'signup' && signupSent && !error && (
            <div style={{...S.errBox, background:'#f0fdf4', borderColor:'#86efac', color:'#166534'}}>
              If this email has been invited, a confirmation link has been sent. Please check your inbox.
            </div>
          )}

          {mode === 'reset' && resetSent ? (
            <div style={{...S.errBox, background:'#f0fdf4', borderColor:'#86efac', color:'#166534'}}>
              ✓ If an account exists for {email}, a reset link has been sent.
            </div>
          ) : (
          <form onSubmit={handleSubmit} style={S.form}>
            {mode === 'signup' && (
              <div>
                <label style={S.label}>Full name</label>
                <input
                  type="text" value={name} onChange={e=>setName(e.target.value)}
                  placeholder="Your name" style={S.input}
                  onFocus={e=>{e.target.style.borderColor='#5DADE2';e.target.style.boxShadow='0 0 0 3px rgba(93,173,226,.18)'}}
                  onBlur={e=>{e.target.style.borderColor='#E1E9F4';e.target.style.boxShadow='none'}}
                />
              </div>
            )}

            <div>
              <label style={S.label}>Email</label>
              <input
                type="email" value={email} onChange={e=>setEmail(e.target.value)} required
                placeholder="you@company.com" style={S.input}
                onFocus={e=>{e.target.style.borderColor='#5DADE2';e.target.style.boxShadow='0 0 0 3px rgba(93,173,226,.18)'}}
                onBlur={e=>{e.target.style.borderColor='#E1E9F4';e.target.style.boxShadow='none'}}
              />
            </div>

            {mode !== 'reset' && (
            <div>
              <label style={S.label}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'} value={password} onChange={e=>setPassword(e.target.value)} required
                  placeholder="••••••••" style={{ ...S.input, paddingRight: 46 }}
                  onFocus={e=>{e.target.style.borderColor='#5DADE2';e.target.style.boxShadow='0 0 0 3px rgba(93,173,226,.18)'}}
                  onBlur={e=>{e.target.style.borderColor='#E1E9F4';e.target.style.boxShadow='none'}}
                />
                <button type="button" onClick={() => setShowPassword(v => !v)}
                  onMouseDown={e => e.preventDefault()}
                  aria-label={showPassword ? 'Hide password' : 'Show password'} title={showPassword ? 'Hide password' : 'Show password'}
                  style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', borderRadius: 8, cursor: 'pointer', color: '#5A6478', padding: 0 }}>
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19M14.12 14.12a3 3 0 11-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            )}

            {mode === 'signin' && (
              <div style={S.rowBetween}>
                <label style={S.rememberLabel}>
                  <input type="checkbox" checked={remember} onChange={e=>setRemember(e.target.checked)}
                    style={{width:15,height:15,accentColor:'#0B3D91',cursor:'pointer'}}/>
                  Remember me
                </label>
                <a href="#" style={S.link} onClick={e=>{e.preventDefault(); setMode('reset'); setError(null); setResetSent(false)}}>Forgot password?</a>
              </div>
            )}

            {mode === 'reset' && (
              <div style={S.rowBetween}>
                <a href="#" style={S.link} onClick={e=>{e.preventDefault(); setMode('signin'); setError(null)}}>← Back to Sign In</a>
              </div>
            )}

            <button type="submit" disabled={loading} style={S.submitBtn(loading)}>
              <span>{loading ? 'Please wait…' : mode==='signin' ? 'Sign In' : mode==='reset' ? 'Send Reset Link' : 'Create Account'}</span>
              {!loading && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12h14M13 6l6 6-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          </form>
          )}

          <p style={S.fcFoot}>· Protected workspace — need access? Contact your admin</p>
        </div>
      </main>
    </div>
  )
}
